import Axios from "@/utils/Axios";
import Axios2 from "@/utils/Axios2";
import * as echarts from 'echarts'
import qs from "qs";

/**
 * 根据日历字符串 (yyyy-MM-dd) 返回当日 00:00:00 和次日 00:00:00 的字符串
 * @param {string} dateStr
 * @returns {{startTime: string, endTime: string}}
 */
export function getDayRange(dateStr) {
  const base = new Date(dateStr);
  const next = new Date(base);
  next.setDate(base.getDate() + 1);

  const pad = n => n.toString().padStart(2, '0');
  const toStr = d =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} 00:00:00`;

  return { startTime: toStr(base), endTime: toStr(next) };
}

/**
 * 处理日历变更
 */
export function handleCalendarChange(context) {
  let currentDate;

  if (context.newtm instanceof Date) {
    currentDate = context.newtm;
  } else {
    currentDate = new Date(context.newtm);
    if (isNaN(currentDate.getTime())) {
      console.warn('Invalid Date, falling back to current date');
      currentDate = new Date();
    }
  }

  const prevDate = new Date(currentDate);
  prevDate.setMonth(prevDate.getMonth() - 1);
  const prevDateStr = `${prevDate.getFullYear()}-${String(prevDate.getMonth() + 1).padStart(2, '0')}-01`;

  const nextDate = new Date(currentDate);
  nextDate.setMonth(nextDate.getMonth() + 1);
  const nextDateStr = `${nextDate.getFullYear()}-${String(nextDate.getMonth() + 1).padStart(2, '0')}-01`;

  return { prevDate:prevDateStr,nextDate:nextDateStr }
}

/**
 * 获取日历数据
 */
export function getCalendarDate(context) {
  let param = {};
  const { prevDate,nextDate } = handleCalendarChange(context);
  param.method="get";
  param.url="api/v1/statistic/monitorDayRiskLevels";
  param.params={
    monitorSiteId:context.saveid,
    startDate:prevDate,
    endDate:nextDate
  }
  return Axios2.ajax(param).then((res)=>{
    context.calendarData = res.data.riskLevelMap || {};
    // ponytail: 时间轴接口通常比日历先回，日历未就绪时会被误判成“不在监测范围”，日历就绪后补算一次。
    // 只在已判越界时补算；判定正确（有隐患数据）时不多打请求。上限：安全日每次日历刷新多一次时间轴请求。
    if (context.outsideMonitorPeriod) {
      getRiskTimePeriodAnalyze(context);
    }
  }).catch((err) => {
    console.error("Failed to load calendar data:", err);
    context.calendarData = {};
  });
}

/**
 * 获取风险等级类
 */
export function getRiskLevelClass(context, dateStr){
  // 确保日期格式一致
  const formattedDate = dateStr.split('-').slice(0, 3).join('-');
  const riskLevels = context.calendarData[formattedDate];
  if(riskLevels && riskLevels.length){
    // 获取最高风险级别
    const highestLevel = Math.max(...riskLevels.filter(level => level !== 6)); // 排除6的影响
    switch(highestLevel) {
      case 1: return 'risk1';
      case 2: return 'risk2';
      case 3: return 'risk3';
      case 4: return 'risk4';
      case 5: return 'risk5';
      default: return '';
    }
  }
  return '';
}

/**
 * 检查特殊标签
 */
export function hasSpecialTag(context, dateStr){
  const formattedDate = dateStr.split('-').slice(0, 3).join('-');
  const riskLevels = context.calendarData[formattedDate];
  return riskLevels && riskLevels.includes(6);
}

/**
 * 获取风险时间分析
 */
export function getRiskTimePeriodAnalyze(context){
  let param = {};
  param.method = "post";
  param.url="/monitorBoard/riskTimePeriodAnalyze";
  const { startTime, endTime } = getDayRange(context.newtm);
  param.data={
    startDateTime:startTime,
    monitorSiteId:context.saveid,
    endDateTime:endTime,
  }
  return Axios.ajax(param).then((res)=>{
    if (res.data && res.data.length > 0){
      context.hasRiskData = true;
      context.timeLineData = res.data;
      context.outsideMonitorPeriod = false;
    } else {
      context.hasRiskData = false;
      // ponytail: 后端无隐患时，根据日历 riskLevelMap 决定是否显示绿色安全状态图
      const dateKey = startTime.split(' ')[0];
      const calendarEntry = context.calendarData && context.calendarData[dateKey];
      if (calendarEntry && calendarEntry.includes(1)) {
        context.outsideMonitorPeriod = false;
        const now = new Date();
        const pad = n => n.toString().padStart(2, '0');
        const todayStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
        const isToday = dateKey === todayStr;
        // ponytail: 今日截断到当前时刻，历史日期到次日 00:00（即当天结束）
        const greenEnd = isToday
          ? `${todayStr} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`
          : endTime;
        context.timeLineData = [{
          groupName: '安全状态',
          items: [{
            riskLevel: 1,
            startDateTime: startTime,
            endDateTime: greenEnd,
            positions: []
          }]
        }];
      } else {
        context.timeLineData = [];
        context.outsideMonitorPeriod = true;
      }
    }
  })
}

/**
 * 初始化时间线图表
 */
export function initTimelineChart(context, riskData = [], is24Hours = false) {
  // 检查数据是否为空
  if (!riskData || riskData.length === 0) {
    context.hasRiskData = false;
    if (context.timelineChart) {
      context.timelineChart.dispose();
      context.timelineChart = null;
    }
    return;
  }

  context.hasRiskData = true;
  // 使用 nextTick 确保 DOM 更新后再初始化图表
  context.$nextTick(() => {
    // 确保在创建新图表前销毁旧的图表实例
    if (context.timelineChart) {
      context.timelineChart.dispose();
      context.timelineChart = null;
    }

    const chartDom = document.getElementById('timelineChart');
    if (!chartDom) {
      console.warn('Timeline chart container not found');
      return;
    }

    // 初始化图表
    context.timelineChart = echarts.init(chartDom);
    const yAxisData = riskData.map(group => group.groupName);
    const seriesData = [];

    // 处理数据
    riskData.forEach((group, groupIndex) => {
      group.items.forEach(item => {
        const startTime = new Date(item.startDateTime);
        const endTime = new Date(item.endDateTime);

        let timeValue;
        if (is24Hours) {
          // 修改24小时视图下的时间计算逻辑
          const baseTime = new Date(context.yesterdayDateTime);
          baseTime.setHours(baseTime.getHours(), 0, 0, 0); // 设置为24小时前的整点时间

          // 计算相对于基准时间的小时差
          const startHourDiff = (startTime - baseTime) / (1000 * 60 * 60);
          const endHourDiff = (endTime - baseTime) / (1000 * 60 * 60);

          // 确保时间范围在0-24小时内
          if (startHourDiff <= 24 && endHourDiff >= 0) {
            timeValue = [
              groupIndex,
              Math.max(0, startHourDiff),
              Math.min(24, endHourDiff)
            ];
          }
        } else {
          // 保持原有的普通视图逻辑不变
          const dayStart = new Date(context.newtm);
          dayStart.setHours(0, 0, 0, 0);
          const nextDayStart = new Date(dayStart);
          nextDayStart.setDate(nextDayStart.getDate() + 1);

          // 计算相对于当天0点的小时数
          const startHour = Math.max(0, (startTime - dayStart) / (1000 * 60 * 60));
          const endHour = Math.min(24, (endTime - dayStart) / (1000 * 60 * 60));

          if (startHour < 24 && endHour > 0) {
            timeValue = [groupIndex, startHour, endHour];
          }
        }

        if (timeValue) {
          seriesData.push({
            name: item.riskName,
            value: [...timeValue, `${item.startDateTime} - ${item.endDateTime}`, item.riskLevel, item.positions.join(', ')],
            itemStyle: {
              normal: {
                color: getRiskColor(item.riskLevel)
              }
            },
          });
        }
      });
    });

    // 图表配置
    const option = {
      tooltip: {
        trigger: 'item',
        formatter: function (params) {
          const data = params.data;
          // 只有当value[5]存在且不为空时才显示位置信息
          const positionsText = data.value[5] ? `<br>隐患位置: ${data.value[5]}` : '';
          return `${data.name}<br>时间: ${data.value[3]}${positionsText}`;
        }
      },
      xAxis: {
        type: 'value',
        min: 0,
        max: 24,
        interval: 1,
        axisLabel: {
          formatter: (value) => {
            const hour = Math.floor(value);
            if (hour % 2 === 0) {
              if (is24Hours) {
                const baseTime = new Date(context.yesterdayDateTime);
                baseTime.setHours(baseTime.getHours(), 0, 0, 0); // 设置为24小时前的整点时间
                const time = new Date(baseTime.getTime() + hour * 60 * 60 * 1000);
                const month = time.getMonth() + 1;
                const day = time.getDate();
                const hourStr = String(time.getHours()).padStart(2, '0');
                return `${month}月${day}日\n${hourStr}:00`;
              } else {
                return hour === 24 ? '24:00' : `${hour}:00`;
              }
            }
            return '';
          },
          textStyle: {
            color: 'rgba(0, 0, 0, 0.5)',
            fontSize: 12
          }
        },
        axisLine: {
          show: true,
          lineStyle: {
            color: '#3462E1'
          }
        },
        axisTick: {
          show: true,
          inside: true,
          length: 6,
          lineStyle: {
            color: '#3462E1'
          }
        },
        minorTick: {
          show: true,
          splitNumber: 4,
          length: 3,
          inside: true,
          lineStyle: {
            color: '#3462E1'
          }
        },
        splitLine: {
          show: false  // 将竖向网格线设置为不显示
        }
      },
      yAxis: {
        type: 'category',
        data: yAxisData,
        axisLine: {
          lineStyle: {
            color: '#E4E7ED'
          }
        },
        axisLabel: {
          color: '#000000',
          fontFamily: 'OPPOSans'
        },
        axisTick: {
          show: false
        },
        // 修改横向网格线的颜色和透明度
        splitLine: {
          show: true,
          lineStyle: {
            color: 'rgba(0, 151, 255, 0.35)', // #0097FF with 35% opacity
            type: 'dashed'
          }
        }
      },
      grid: {
        top: 20,
        bottom: 60,
        left: 60,
        right: 60,
        containLabel: true,
        height: is24Hours ? yAxisData.length * 50 + 37 : yAxisData.length * 50 + 25, // 根据视图类型设置高度
        show: true,
        backgroundColor: {
          type: 'linear',
          x: 0,
          y: 0,
          x2: 0,
          y2: 1,
          colorStops: [{
            offset: 0,
            color: '#FFFFFF' // 0% 处的颜色
          }, {
            offset: 1,
            color: '#F1F8FF' // 100% 处的颜色
          }]
        }
      },
      series: [{
        name: '隐患时段',
        type: 'custom',
        renderItem: function (params, api) {
          const categoryIndex = api.value(0);
          const start = api.coord([api.value(1), categoryIndex]);
          const end = api.coord([api.value(2), categoryIndex]);
          const height = 50;

          const riskLevel = api.value(4);
          const positions = api.value(5);
          const colorMapping = {
            1: 'rgba(2, 182,  27, 0.2)',
            2: 'rgba(0, 107, 195, 0.2)',
            3: 'rgba(255, 176, 0, 0.2)',
            4: 'rgba(231,43,9,0.2)',
            5: 'rgba(231,43,9,0.55)',
            6: 'rgb(216,216,216,0.55)'
          };
          const fillColor = colorMapping[riskLevel] || '#CCCCCC';

          // 计算文本显示区域的宽度
          const blockWidth = Math.max(1, end[0] - start[0]);
          const textWidth = blockWidth - 20; // 左右各留10px边距

          // 创建临时canvas计算文本宽度
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');
          context.font = '18px OPPOSans M';

          // 处理文本截断
          let displayText = positions || '';
          const textMetrics = context.measureText(displayText);
          if (textMetrics.width > textWidth) {
            // 逐个字符测试，找到合适的截断位置
            for (let i = 0; i < displayText.length; i++) {
              const testText = displayText.slice(0, i) + '...';
              if (context.measureText(testText).width > textWidth) {
                displayText = displayText.slice(0, i - 1) + '...';
                break;
              }
            }
          }

          return {
            type: 'group',
            children: [
              {
                type: 'rect',
                shape: {
                  x: start[0],
                  y: start[1] - height / 2,
                  width: blockWidth,
                  height: height,
                  r: 0
                },
                style: api.style()
              },
              {
                type: 'rect',
                shape: {
                  x: start[0],
                  y: start[1] - height / 2,
                  width: 2,
                  height: height,
                  r: 0
                },
                style: {
                  fill: fillColor
                }
              },
              {
                type: 'text',
                style: {
                  text: displayText,
                  fill: 'rgba(0, 0, 0, 0.65)',
                  font: '18px OPPOSans M',
                  x: start[0] + 10,
                  y: start[1] + 1,
                  textAlign: 'left',
                  textBaseline: 'middle'
                }
              }
            ]
          };
        },
        encode: {
          x: [1, 2],
          y: 0
        },
        data: seriesData
      }]
    };

    // 设置图表配置
    context.timelineChart.setOption(option);

    // 添加resize事件监听
    const resizeHandler = () => {
      if (context.timelineChart) {
        context.timelineChart.resize();
      }
    };

    window.removeEventListener('resize', resizeHandler);
    window.addEventListener('resize', resizeHandler);

    // 保存resize处理函数以便后续清理
    context.resizeHandler = resizeHandler;
    // 计算图表所需高度
    // 基础高度 100px + 每个风险类型占用 50px + 上下边距 60px
    const baseHeight = 100;
    const itemHeight = 50;
    const padding = 30;

    if (riskData && riskData.length > 0) {
      // 根据数据条数计算所需高度
      const dataHeight = riskData.length * itemHeight;
      context.timelineChartHeight = Math.max(400, baseHeight + dataHeight + padding);
    } else {
      context.timelineChartHeight = 400; // 无数据时使用默认高度
    }
  });
}

/**
 * 根据riskLevel返回对应的颜色
 */
export function getRiskColor(riskLevel) {
  // 根据riskLevel返回对应的颜色
  if (riskLevel === 2) {
    return 'rgba(0, 107, 195, 0.15)'; // 早期风险
  } else if (riskLevel === 3) {
    return 'rgba(255, 176, 0, 0.15)'; // 一般风险
  } else if (riskLevel === 4) {
    return 'rgba(231,43,9,0.15)'; // 较大风险
  } else if (riskLevel === 5){
    return 'rgba(255, 0, 0, 0.5)'; // 严重风险
  } else if (riskLevel === 6){
    return 'rgba(216,216,216,0.5)'; // 误报源
  } else if (riskLevel === 1){
    return 'rgba(2,182,27,0.15)'; // 误报源
  }
}

/**
 * 获取电气火灾风险数据
 */
export function getFireData(context){
  let param = {};
  param.method = "post";
  param.url = "/analogData/hourFreezeDataInfoV2";
  param.data = qs.stringify({
    monitorSiteId: context.saveid,
    searchDate: context.newtm,
    'params[0]': "ElectricalFire"
  })
  return Axios.ajax(param).then((res) => {
    context.FireData = res.data;
  })
}

/**
 * 获取合相有功电能
 */
export function getEnergyData(context){
  let param = {};
  param.method = "post";
  param.url = "/analogData/hourFreezeDataInfoV2";
  param.data = qs.stringify({
    monitorSiteId: context.saveid,
    searchDate: context.newtm,
    'params[0]': "P_total"
  })
  return Axios.ajax(param).then((res) => {
    if (!res.data) return;
    context.EnergyData = res.data;
  })
}

/**
 * 获取能源趋势
 */
export function getEnergyTrend(context){
  let param = {};
  param.method = "post";
  param.url = "/energy/monitorHourEnergyTrend";
  param.data = {
    localDate: context.newtm,
    monitorSiteId: context.saveid
  }
  return Axios.ajax(param).then((res) => {
    context.EnergyTrend = res.data;
    console.log('成功获取能源趋势数据：', context.EnergyTrend)
  })
}
